import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Checkbox,
  ListSubheader,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  getWorkspaceId,
  useAppSelector,
  usePriceListMutation,
} from '@zonofi/seller-rtk-store';
import { debounce } from 'lodash';
import { SHARED_SVGS } from '../assets';

export interface MultiPriceListOption {
  id: string;
  title: string;
  subtitle: string;
  date: string;
  priceListId: string;
}

export interface MultiPriceListValue {
  id: string;
  title: string;
}

interface MultiPriceListDropdownProps {
  onChange: (selectedOptions: MultiPriceListOption[]) => void;
  placeholder?: string;
  className?: string;
  values?: MultiPriceListValue[] | null;
  screen?: 'CUSTOMERS' | 'CFA';
  disabled?: boolean;
}

const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const StyledAutocomplete = styled(
  Autocomplete<MultiPriceListOption, true, false, false>
)({
  flex: 1,
  '& .MuiInputBase-root': {
    padding: 0,
  },
  '& .MuiChip-root': {
    flexShrink: 0,
  },
});

export const MultiPriceListDropdown: React.FC<MultiPriceListDropdownProps> = ({
  onChange,
  placeholder = 'Select Price List',
  className = '',
  values = null,
  screen,
  disabled = false,
}) => {
  const [options, setOptions] = useState<MultiPriceListOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<
    MultiPriceListOption[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const workspaceId = useAppSelector(getWorkspaceId);
  const [priceListReq, priceListResponse] = usePriceListMutation();

  // Sync parent values against loaded options
  useEffect(() => {
    if (values && options.length > 0) {
      const resolved = values
        .map(
          (v) =>
            options.find((o) => o.id === v.id || o.priceListId === v.id) ?? null
        )
        .filter(Boolean) as MultiPriceListOption[];
      setSelectedOptions(resolved);
    }
    if (!values || values.length === 0) {
      setSelectedOptions([]);
    }
  }, [values, options]);

  const debouncedSearch = useMemo(
    () =>
      debounce((searchKey: string) => {
        priceListReq({
          priceListRequest: {
            sellerWorkspaceId: workspaceId,
            workspaceId,
            pageNo: 1,
            pageSize: 20,
            searchKey,
            criteria:
              screen === 'CUSTOMERS'
                ? ['customer-price', 'customer-segment']
                : screen === 'CFA'
                  ? ['cfa-price']
                  : [],
            status: 'Active',
          },
        });
      }, 500),
    [priceListReq, workspaceId, screen]
  );

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch]
  );

  // Fetch initial data on mount to display existing price lists
  useEffect(() => {
    if (workspaceId) {
      priceListReq({
        priceListRequest: {
          sellerWorkspaceId: workspaceId,
          workspaceId,
          pageNo: 1,
          pageSize: 20,
          criteria:
            screen === 'CUSTOMERS'
              ? ['customer-price', 'customer-segment']
              : screen === 'CFA'
                ? ['cfa-price']
                : [],
          status: 'Active',
        },
      });
    }
  }, [workspaceId, screen]);

  // Refetch when dropdown opens (for fresh data)
  useEffect(() => {
    if (isOpen && workspaceId) {
      priceListReq({
        priceListRequest: {
          sellerWorkspaceId: workspaceId,
          workspaceId,
          pageNo: 1,
          pageSize: 20,
          criteria:
            screen === 'CUSTOMERS'
              ? ['customer-price', 'customer-segment']
              : screen === 'CFA'
                ? ['cfa-price']
                : [],
          status: 'Active',
        },
      });
    }
  }, [isOpen, workspaceId, screen]);

  useEffect(() => {
    if (priceListResponse.isSuccess) {
      const formatted: MultiPriceListOption[] = (
        priceListResponse.data?.pricing || []
      )
        .filter((p) => p.isActive)
        .map((p) => ({
          id: p.code,
          title: p.name,
          subtitle: p.criteria,
          date: p.createdOn,
          priceListId: p.priceListId,
        }));
      setOptions(formatted);
    }
  }, [priceListResponse]);

  const handleChange = (_: any, newValues: MultiPriceListOption[]) => {
    setSelectedOptions(newValues);
    onChange(newValues);
  };

  const handleSelectAll = () => {
    setSelectedOptions(options);
    onChange(options);
  };

  const handleClear = () => {
    setSelectedOptions([]);
    onChange([]);
  };

  return (
    <StyledAutocomplete
      className={className}
      multiple
      disableCloseOnSelect
      options={options}
      getOptionLabel={(option) => option.title}
      value={selectedOptions}
      filterOptions={(opts) => opts}
      onChange={handleChange}
      onInputChange={(_, newInputValue, reason) => {
        setSearchTerm(newInputValue);
        if (reason === 'input') debouncedSearch(newInputValue);
      }}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      openOnFocus
      disabled={disabled}
      loading={priceListResponse.isLoading}
      clearOnEscape={false}
      clearIcon={null}
      isOptionEqualToValue={(option, val) =>
        option.id === val.id || option.priceListId === val.priceListId
      }
      popupIcon={
        <img src={SHARED_SVGS.SelectArrowUp} width={16} height={16} alt="" />
      }
      groupBy={() => 'priceLists'}
      renderGroup={(params) => (
        <>
          <ListSubheader
            key="header"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0px 16px',
              lineHeight: '40px',
              backgroundColor: '#fff',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            {searchTerm.length === 0 && (
              <span
                style={{
                  fontSize: '13px',
                  color: '#595959',
                  fontFamily: "'Segoe-UI-VF', sans-serif",
                }}
                className="primary-font-regular"
              >
                Price Lists ({selectedOptions.length}/{options.length})
              </span>
            )}
            <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
              <span
                onClick={handleSelectAll}
                style={{
                  fontSize: '13px',
                  color: '#32A7E8',
                  cursor: 'pointer',
                  fontFamily: "'Segoe-UI-VF', sans-serif",
                }}
                className="primary-font-regular"
              >
                Select All
              </span>
              <span
                onClick={handleClear}
                style={{
                  fontSize: '13px',
                  color: '#DD360C',
                  cursor: 'pointer',
                  fontFamily: "'Segoe-UI-VF', sans-serif",
                }}
                className="primary-font-regular"
              >
                Clear
              </span>
            </div>
          </ListSubheader>
          {params.children}
        </>
      )}
      renderOption={(props, option, { selected }) => {
        const { key, ...otherProps } = props as any;
        return (
          <li {...otherProps} key={option.id} style={{ padding: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #f7fafc',
                backgroundColor: selected ? '#ebf8ff' : '#fff',
                width: '100%',
              }}
            >
              {/* Checkbox */}
              <Checkbox
                icon={uncheckedIcon}
                checkedIcon={checkedIcon}
                checked={selected}
                size="small"
                sx={{
                  padding: '0 8px 0 0',
                  color: '#CCCCCC',
                  '&.Mui-checked': { color: '#32A7E8' },
                }}
              />

              {/* Icon avatar */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '10px',
                  minWidth: '36px',
                  minHeight: '36px',
                  borderRadius: '50%',
                  backgroundColor: '#f5f5f5',
                  flexShrink: 0,
                }}
              >
                <img
                  src={SHARED_SVGS.PriceList}
                  width="20"
                  height="20"
                  alt="price-list-icon"
                />
              </div>

              {/* Title + subtitle */}
              <div style={{ flex: 1 }}>
                <div
                  className="primary-font-semibold"
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: selected ? '#3182ce' : '#1a202c',
                    marginBottom: '2px',
                  }}
                >
                  {option.title}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '12px',
                    color: '#718096',
                    gap: '4px',
                  }}
                >
                  <span className="primary-font-regular">
                    {option.subtitle}
                  </span>
                  <span>•</span>
                  <span className="primary-font-regular">
                    {new Date(option.date).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            </div>
          </li>
        );
      }}
      renderTags={(tagValues, getTagProps) =>
        tagValues.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.title}
            size="small"
            sx={{
              borderRadius: '4px',
              height: 20,
              fontSize: '0.7rem',
              backgroundColor: '#f0f0f0',
              border: '1px solid #f0f0f0',
              color: '#000',
              fontFamily: 'inherit',
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder={selectedOptions.length === 0 ? placeholder : ''}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
          }}
          className="primary-font-regular seller-font-size-regular"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'Segoe-UI-VF',
              '& input': {
                fontFamily: 'Segoe-UI-VF',
              },

              '& input::placeholder': {
                fontFamily: 'Segoe-UI-VF',
              },

              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #dfe0e0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: '1px solid rgba(0,0,0,0.23)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #32A7E8',
              },
            },
          }}
        />
      )}
      sx={{
        '& .MuiInputBase-root': { padding: 0 },
        '& .MuiAutocomplete-listbox': {
          padding: 0,
          '& .MuiAutocomplete-option': {
            padding: 0,
            '&:hover': { backgroundColor: '#f5f5f5' },
            '&[aria-selected="true"]': { backgroundColor: '#fff' },
          },
        },
      }}
    />
  );
};
